import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  resolveListLimit,
  SHOP_LIST_PAGE_SIZE,
} from "./paginatedList";

export const DEFAULT_LIST_SORT = "topRated";

export type ShopListQuery = {
  page: number;
  limit: number;
  q: string;
  sort: string;
  cat: string | null;
};

export function parseListPage(raw: string | null): number {
  const page = Number(raw);
  return Number.isInteger(page) && page > 0 ? page : 1;
}

export function parseListLimit(raw: string | null): number {
  return resolveListLimit(raw == null || raw === "" ? undefined : Number(raw));
}

export function readShopListQuery(params: URLSearchParams): ShopListQuery {
  return {
    page: parseListPage(params.get("page")),
    limit: parseListLimit(params.get("limit")),
    q: params.get("q") ?? "",
    sort: params.get("sort") || DEFAULT_LIST_SORT,
    cat: params.get("cat"),
  };
}

export function shopListQueryToParams(query: ShopListQuery): URLSearchParams {
  const next = new URLSearchParams();
  if (query.q) next.set("q", query.q);
  if (query.sort && query.sort !== DEFAULT_LIST_SORT) {
    next.set("sort", query.sort);
  }
  if (query.cat) next.set("cat", query.cat);
  if (query.page > 1) next.set("page", String(query.page));
  if (query.limit && query.limit !== SHOP_LIST_PAGE_SIZE) {
    next.set("limit", String(query.limit));
  }
  return next;
}

/**
 * List page / search / sort / category live in the URL so back from a
 * detail page (and shared links) restore the same view.
 */
export function useShopListQuery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useMemo(() => readShopListQuery(searchParams), [searchParams]);

  const replaceQuery = useCallback(
    (patch: Partial<ShopListQuery>) => {
      setSearchParams((prev) => {
        const merged = { ...readShopListQuery(prev), ...patch };
        return shopListQueryToParams(merged);
      }, { replace: true });
    },
    [setSearchParams]
  );

  const setPage = useCallback(
    (page: number) => replaceQuery({ page }),
    [replaceQuery]
  );

  const setLimit = useCallback(
    (limit: number) => replaceQuery({ limit: resolveListLimit(limit), page: 1 }),
    [replaceQuery]
  );

  const setSearch = useCallback(
    (q: string) => replaceQuery({ q, page: 1 }),
    [replaceQuery]
  );

  const setSort = useCallback(
    (sort: string) => replaceQuery({ sort, page: 1 }),
    [replaceQuery]
  );

  const setCategoryId = useCallback(
    (cat: string | null) => replaceQuery({ cat, page: 1 }),
    [replaceQuery]
  );

  return {
    page: query.page,
    limit: query.limit,
    search: query.q,
    sort: query.sort,
    categoryId: query.cat,
    setPage,
    setLimit,
    setSearch,
    setSort,
    setCategoryId,
  };
}
